/**
 * Pan-delta math for the top-down planform viewer (orthographic camera).
 *
 * The viewer maps screen axes to world axes crosswise — screen-vertical drives
 * world X, screen-horizontal drives world Y (aircraft X runs up the screen).
 * Each pixel delta must be normalized by its OWN screen axis (the vertical
 * delta spans the canvas height, the horizontal delta spans the width) and then
 * scaled by the world extent of the world axis it drives:
 *   - vertical pixels   → world X, scaled by worldH (top-bottom extent)
 *   - horizontal pixels → world Y, scaled by worldW (left-right extent)
 *
 * FXC-9005: the world-extent factors used to be crossed (worldW with the
 * vertical delta, worldH with the horizontal delta), so on any non-square panel
 * the geometry drifted by the canvas aspect ratio while panning.
 */
export type PanInput = {
  /** Horizontal pixel delta (clientX now − start). */
  pxDx: number;
  /** Vertical pixel delta (clientY now − start). */
  pxDy: number;
  /** Canvas width in CSS pixels. */
  rectWidth: number;
  /** Canvas height in CSS pixels. */
  rectHeight: number;
  /** Orthographic world width (|right − left|). */
  worldW: number;
  /** Orthographic world height (|top − bottom|). */
  worldH: number;
};

/**
 * @returns world-space camera position deltas for a planform pan move.
 *   dx applies to camera.position.x, dy applies to camera.position.y.
 */
export function computePanDelta({
  pxDx,
  pxDy,
  rectWidth,
  rectHeight,
  worldW,
  worldH,
}: PanInput): { dx: number; dy: number } {
  const dx = rectHeight === 0 ? 0 : (pxDy / rectHeight) * worldH;
  const dy = rectWidth === 0 ? 0 : (pxDx / rectWidth) * worldW;
  return { dx, dy };
}
